/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Seg } from "./widgets";
import { dash, fmtDate, formatLabel, formatShort, n0, nz } from "./model";
import { getMatches } from "./api";

type Status = "ENDED" | "IN_PROGRESS" | "SCHEDULED";
const PAGE = 10;
const scoreOf = (m: any, team?: any) => {
  const i = nz<any>(m.inningScorecardList).find((x) => (team?.teamSnapshotId && x.battingTeamId === team.teamSnapshotId) || (!!team?.teamName && String(x.battingTeamName ?? "").trim().toLowerCase() === String(team.teamName).trim().toLowerCase()));
  return i ? `${i.runs}/${i.wicket} (${i.over}.${n0(i.ball)}${i.maxOvers ? `/${i.maxOvers}` : ""})` : dash;
};

const RESULT: Record<string, { label: string; short: string; word: string }> = {
  W: { label: "W", short: "W", word: "Won" },
  L: { label: "L", short: "L", word: "Lost" },
  WFI: { label: "W", short: "1st", word: "Drawn, won on first-innings lead" },
  LFI: { label: "L", short: "1st", word: "Drawn, lost on first-innings lead" },
  T: { label: "T", short: "", word: "Tied" },
  D: { label: "D", short: "", word: "Drawn" },
  NR: { label: "NR", short: "", word: "No result" },
};

/** Runs ahead of the other side after the first innings of each (positive = the player's side led). */
const firstInningsLead = (m: any): number | null => {
  const inns = nz<any>(m.inningScorecardList).slice().sort((a, b) => n0(a.inningNumber) - n0(b.inningNumber));
  const mine = [m.teamOne, m.teamTwo].find((t) => t?.teamSnapshotId && t.teamSnapshotId === m.playerTeamSnapshotId);
  const other = [m.teamOne, m.teamTwo].find((t) => t && t !== mine);
  const first = (t: any) => inns.find((i) => (t?.teamSnapshotId && i.battingTeamId === t.teamSnapshotId) || String(i.battingTeamName ?? "").trim().toLowerCase() === String(t?.teamName ?? "").trim().toLowerCase());
  const a = first(mine), b = first(other);
  return a && b ? n0(a.runs) - n0(b.runs) : null;
};

/**
 * Outcome badge so a result reads at a glance. Solid W / L = outright win or loss; outlined W / L tagged "1st" = a drawn
 * match decided on first-innings lead; grey D = drawn, T = tied, NR = no result. Full wording is in the tooltip.
 */
const ResultBadge = ({ m, status }: { m: any; status: Status }) => {
  if (status === "IN_PROGRESS") return <span className="tag live"><span className="ldot on" />LIVE</span>;
  if (status === "SCHEDULED") return <span className="ft-note">Upcoming</span>;
  // first-innings-lead outcomes only exist in multi-day matches; everywhere else the result is simply W or L
  const multiDay = /TEST/i.test(m.matchType ?? m.tournament?.matchType ?? "") || n0(m.scheduledDays) > 1;
  const code: string | undefined = !multiDay && m.playerResult === "WFI" ? "W" : !multiDay && m.playerResult === "LFI" ? "L" : m.playerResult;
  const r = code ? RESULT[code] : undefined;
  if (!r) return <span className="s">{m.matchInfo || m.equation || m.matchResult || dash}</span>;
  const lead = code === "WFI" || code === "LFI" ? firstInningsLead(m) : null;
  const tip = [r.word, lead != null && lead !== 0 ? `${Math.abs(lead)} run${Math.abs(lead) === 1 ? "" : "s"} first-innings ${lead > 0 ? "lead" : "deficit"}` : null, code === "W" || code === "L" ? m.equation : null].filter(Boolean).join(" · ");
  return (
    <span className={`rbadge rbadge-${code}`} title={tip} aria-label={tip}>
      {r.label}{r.short && <small>{r.short}</small>}
    </span>
  );
};

const MatchesPane = ({ playerId }: { playerId: string }) => {
  const nav = useNavigate();
  const [status, setStatus] = useState<Status>("ENDED");
  const [fmt, setFmt] = useState("all");
  const [items, setItems] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const load = async (st: Status, p: number) => {
    setLoading(true); setError(false);
    try {
      const res = await getMatches(playerId, st, p, PAGE);
      const list = nz<any>(res.matchResponseList);
      setItems((prev) => (p === 0 ? list : [...prev, ...list]));
      setMore(list.length === PAGE); setPage(p);
    } catch { setError(true); } finally { setLoading(false); }
  };
  useEffect(() => { setItems([]); void load(status, 0); // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [status, playerId]);

  const formats = Array.from(new Set(items.map((m) => m.tournament?.matchType ?? m.matchType).filter(Boolean))) as string[];
  const list = items.filter((m) => fmt === "all" || (m.tournament?.matchType ?? m.matchType) === fmt);

  return (
    <Box title="Matches" sub={status === "ENDED" ? `${list.length} shown · W won · L lost · 1st = drawn, decided on first-innings lead · D drawn · T tied` : `${list.length} shown`} right={<><Seg options={[["ENDED", "Results"], ["IN_PROGRESS", "Live"], ["SCHEDULED", "Upcoming"]]} value={status} onChange={setStatus} />{formats.length > 1 && <Seg options={[["all", "All"], ...formats.map((f) => [f, formatShort(f)] as [string, string])] as any} value={fmt} onChange={setFmt} />}</>}>
      <div className="tw"><table>
        <thead><tr><th>Match</th><th>Format</th><th className="l">Competition</th><th>Date</th><th>Result</th><th><span className="visually-hidden">Details</span></th></tr></thead>
        <tbody>
          {list.map((m) => {
            const id = m.matchId, isOpen = open === id;
            return (
              <FragmentRow key={id} open={isOpen} toggle={() => setOpen(isOpen ? null : id)}
                cells={<>
                  <td><div className="mtx"><b><span className={m.playerTeamSnapshotId && m.playerTeamSnapshotId === m.teamOne?.teamSnapshotId ? "mine" : ""}>{m.teamOne?.teamName}</span> vs <span className={m.playerTeamSnapshotId && m.playerTeamSnapshotId === m.teamTwo?.teamSnapshotId ? "mine" : ""}>{m.teamTwo?.teamName}</span></b><span>{m.groundId?.groundName ?? m.city ?? ""}</span></div></td>
                  <td><span className="fmt">{formatShort(m.tournament?.matchType ?? m.matchType)}</span></td>
                  <td className="s l">{m.tournament?.tournamentName ?? m.leagueName ?? dash}</td>
                  <td className="s">{fmtDate(m.startDate)}</td>
                  <td><ResultBadge m={m} status={status} /></td>
                </>}
                detail={<>
                  <div className="det-grid">
                    <div>{m.teamOne?.teamName}<b>{scoreOf(m, m.teamOne)}</b></div>
                    <div>{m.teamTwo?.teamName}<b>{scoreOf(m, m.teamTwo)}</b></div>
                    <div>{formatLabel(m.tournament?.matchType ?? m.matchType)}<b>{m.equation || m.matchResult || m.tossResult || dash}</b></div>
                  </div>
                  <div className="det-acts"><button className="btn" onClick={() => nav(`/matches-details/${id}`)}>Full scorecard</button></div>
                </>} />
            );
          })}
          {!loading && !list.length && <tr><td colSpan={6} className="s" style={{ textAlign: "center", padding: 24 }}>{error ? "Couldn't load matches." : "No matches for this filter."}</td></tr>}
        </tbody>
      </table></div>
      {loading && <p className="acap" style={{ padding: 16, textAlign: "center" }}>Loading…</p>}
      {!loading && more && <div style={{ textAlign: "center", padding: 14 }}><button className="btn" onClick={() => load(status, page + 1)}>Load more</button></div>}
    </Box>
  );
};

const CHEV = (<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const FragmentRow = ({ cells, detail, open, toggle }: { cells: React.ReactNode; detail: React.ReactNode; open: boolean; toggle: () => void }) => (
  <>
    <tr className="click" tabIndex={0} aria-expanded={open} onClick={toggle} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}>{cells}<td className="chev">{CHEV}</td></tr>
    {open && <tr className="det"><td colSpan={6}>{detail}</td></tr>}
  </>
);
export default MatchesPane;
